// Сохранение пройденного урока или его части при плохой сети (тикет 70,
// части урока .scratch/lesson-parts/plan.md).
// Если completeLessonPart не долетел до сервера, запись встаёт в очередь в
// localStorage и уходит при возврате сети или при следующем открытии
// приложения (LessonSyncFlusher в layout). Повтор несёт `since` - время
// первой попытки: сервер не записывает урок второй раз, если он уже записан
// после этого момента (ответ мог потеряться по дороге назад); повтор частей
// 1 и 2 отсекает сама функция complete_lesson_part (already_saved).

import type { LessonPart } from '@/lib/lessons/parts'

export type PendingLesson = {
  lessonId: string
  /** Часть урока; у записей до частей нет, это весь урок (часть 3). */
  part?: LessonPart
  score: number
  vocabIds: string[]
  timezone: string
  seconds: number
  missedIds: string[]
  /** ISO-время первой попытки сохранить. */
  since: string
}

type KV = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export const QUEUE_KEY = 'tk-lesson-queue-v1'
/** Старше недели не отправляем: прогресс уже не к месту, а серия посчитается не тем днём. */
export const MAX_AGE_MS = 7 * 24 * 3600 * 1000

export function partOf(p: Pick<PendingLesson, 'part'>): LessonPart {
  return p.part ?? 3
}

function store(): KV | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

export function readQueue(kv: KV | null = store(), now = Date.now()): PendingLesson[] {
  if (!kv) return []
  try {
    const raw = kv.getItem(QUEUE_KEY)
    const list = raw ? (JSON.parse(raw) as PendingLesson[]) : []
    if (!Array.isArray(list)) return []
    return list.filter((p) => p && typeof p.lessonId === 'string' && now - Date.parse(p.since) < MAX_AGE_MS)
  } catch {
    return []
  }
}

function writeQueue(list: PendingLesson[], kv: KV | null) {
  if (!kv) return
  try {
    if (list.length) kv.setItem(QUEUE_KEY, JSON.stringify(list))
    else kv.removeItem(QUEUE_KEY)
  } catch {
    /* приватный режим или переполнено: просто не сохраним */
  }
}

const sameKey = (a: Pick<PendingLesson, 'lessonId' | 'part'>, b: Pick<PendingLesson, 'lessonId' | 'part'>) =>
  a.lessonId === b.lessonId && partOf(a) === partOf(b)

/**
 * Кладёт урок или часть в очередь; одна запись на урок и часть (новая заменяет
 * старую, `since` берётся раннее). Части одного урока идут по порядку: очередь
 * хранит их отдельно, flushQueue отправляет в порядке добавления.
 */
export function enqueue(item: PendingLesson, kv: KV | null = store()) {
  const list = readQueue(kv)
  const prev = list.find((p) => sameKey(p, item))
  const merged = prev && prev.since < item.since ? { ...item, since: prev.since } : item
  writeQueue([...list.filter((p) => !sameKey(p, item)), merged], kv)
}

/** Снимает запись урока; без part снимает все его части. */
export function dequeue(lessonId: string, kv: KV | null = store(), part?: LessonPart) {
  writeQueue(
    readQueue(kv).filter((p) => p.lessonId !== lessonId || (part !== undefined && partOf(p) !== part)),
    kv,
  )
}

/** Есть ли в очереди этот урок (любая часть или указанная). */
export function isQueued(lessonId: string, kv: KV | null = store(), part?: LessonPart): boolean {
  return readQueue(kv).some((p) => p.lessonId === lessonId && (part === undefined || partOf(p) === part))
}

/** Повтор с паузой: до `tries` попыток, пауза растёт (1 с, 3 с). */
export async function withRetry<T>(fn: () => Promise<T>, tries = 3, wait = (ms: number) => new Promise((r) => setTimeout(r, ms))): Promise<T> {
  let last: unknown
  for (let i = 0; i < tries; i++) {
    try {
      return await fn()
    } catch (e) {
      last = e
      if (e instanceof Error && e.message === 'Unauthorized') break
      if (i < tries - 1) await wait(i === 0 ? 1000 : 3000)
    }
  }
  throw last
}

/** Отправляет всю очередь; что не ушло, остаётся. Возвращает число отправленных. */
export async function flushQueue(
  send: (p: PendingLesson) => Promise<unknown>,
  kv: KV | null = store(),
): Promise<number> {
  let sent = 0
  for (const p of readQueue(kv)) {
    try {
      await send(p)
      dequeue(p.lessonId, kv, partOf(p))
      sent++
    } catch {
      /* сеть ещё не вернулась - попробуем в следующий раз */
    }
  }
  return sent
}
